import React, { createContext, useContext, useState, useEffect, useRef } from 'react'
import homepageAudio from '../assets/sounds/bgmusic.mp3'
import squareSpawnAudio from '../assets/sounds/squarespawn.mp3'

const AudioContext = createContext()

export const useAudio = () => {
  const context = useContext(AudioContext)
  if (!context) {
    throw new Error('useAudio must be used within an AudioProvider')
  }
  return context
}

export const AudioProvider = ({ children }) => {
  const [isEnabled, setIsEnabled] = useState(() => {
    // Get stored preference or default to true
    const stored = localStorage.getItem('backgroundMusicEnabled')
    return stored !== null ? JSON.parse(stored) : true
  })
  const [isSquareSpawnEnabled, setIsSquareSpawnEnabled] = useState(() => {
    // Get stored preference or default to true
    const stored = localStorage.getItem('squareSpawnSoundEnabled')
    return stored !== null ? JSON.parse(stored) : true
  })
  const [isPlaying, setIsPlaying] = useState(false)
  const [hasPendingPlay, setHasPendingPlay] = useState(false)
  const audioRef = useRef(null)
  const squareSpawnAudioRef = useRef(null)

  // Initialize audio
  useEffect(() => {
    audioRef.current = new Audio(homepageAudio)
    audioRef.current.loop = true
    audioRef.current.volume = 0.135 // Increased by 50% from 0.09

    squareSpawnAudioRef.current = new Audio(squareSpawnAudio)
    squareSpawnAudioRef.current.volume = 0.25 // Lower volume - 50% of previous 50%

    // Clean up on unmount
    return () => {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current = null
      }
      if (squareSpawnAudioRef.current) {
        squareSpawnAudioRef.current.pause()
        squareSpawnAudioRef.current = null
      }
    }
  }, [])

  // Set up user interaction handler to retry music playback
  useEffect(() => {
    const handleUserInteraction = async () => {
      if (hasPendingPlay && isEnabled && audioRef.current) {
        try {
          await audioRef.current.play()
          setIsPlaying(true)
          setHasPendingPlay(false)
        } catch (error) {
          console.log('Retry audio playback failed:', error)
        }
      }
    }

    if (hasPendingPlay) {
      // Listen for user interactions
      const events = ['click', 'touchstart', 'keydown']
      events.forEach(event => {
        document.addEventListener(event, handleUserInteraction, { once: true })
      })

      return () => {
        events.forEach(event => {
          document.removeEventListener(event, handleUserInteraction)
        })
      }
    }
  }, [hasPendingPlay, isEnabled])

  // Save preferences to localStorage
  useEffect(() => {
    localStorage.setItem('backgroundMusicEnabled', JSON.stringify(isEnabled))
  }, [isEnabled])

  useEffect(() => {
    localStorage.setItem('squareSpawnSoundEnabled', JSON.stringify(isSquareSpawnEnabled))
  }, [isSquareSpawnEnabled])

  const playMusic = async () => {
    if (!audioRef.current || !isEnabled) return

    try {
      await audioRef.current.play()
      setIsPlaying(true)
      setHasPendingPlay(false)
    } catch (error) {
      console.log('Audio playback failed, will retry on user interaction:', error)
      // Modern browsers require user interaction before playing audio
      setIsPlaying(false)
      setHasPendingPlay(true)
    }
  }

  const stopMusic = () => {
    if (!audioRef.current) return

    audioRef.current.pause()
    audioRef.current.currentTime = 0
    setIsPlaying(false)
    setHasPendingPlay(false)
  }

  const toggleMusic = () => {
    setIsEnabled(!isEnabled)
    if (!isEnabled) {
      // Will enable and play
      playMusic()
    } else {
      // Will disable and stop
      stopMusic()
    }
  }

  const playSquareSpawnSound = () => {
    if (!squareSpawnAudioRef.current || !isSquareSpawnEnabled) return

    try {
      // Reset the audio to beginning and play
      squareSpawnAudioRef.current.currentTime = 0
      squareSpawnAudioRef.current.play()
    } catch (error) {
      console.log('Square spawn sound playback failed:', error)
    }
  }

  const toggleSquareSpawnSound = () => {
    setIsSquareSpawnEnabled(!isSquareSpawnEnabled)
  }

  const value = {
    isEnabled,
    isPlaying,
    playMusic,
    stopMusic,
    toggleMusic,
    playSquareSpawnSound,
    isSquareSpawnEnabled,
    toggleSquareSpawnSound
  }

  return (
    <AudioContext.Provider value={value}>
      {children}
    </AudioContext.Provider>
  )
}